import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  CreditCard, 
  CheckCircle2, 
  Clock,
  User,
  Calendar,
  Building2,
  RefreshCw
} from 'lucide-react';
import { useCurrency } from '@/context/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPaymentsPage = () => {
  const { formatPrice } = useCurrency();
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmingId, setConfirmingId] = useState(null);

  useEffect(() => {
    fetchPendingPayments();
  }, []);

  const fetchPendingPayments = async () => {
    try {
      const response = await axios.get(`${API}/admin/bookings/pending-payments`);
      setPendingPayments(response.data);
    } catch (error) {
      console.error('Error fetching pending payments:', error);
      toast.error('Failed to load pending payments');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async (bookingId) => {
    setConfirmingId(bookingId);
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/confirm-payment`);
      toast.success('Payment confirmed successfully');
      fetchPendingPayments();
    } catch (error) {
      console.error('Error confirming payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm payment');
    } finally {
      setConfirmingId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-payments-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Pending Payments</h1>
            <p className="text-muted-foreground mt-1">Confirm manual bank transfer payments</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchPendingPayments} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Badge variant="outline" className="text-sm">
              <Clock className="w-4 h-4 mr-1" />
              {pendingPayments.length} Pending
            </Badge>
          </div>
        </div>

        {/* Bank Details Info */}
        <div className="card-clinical bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-sky-100 dark:bg-sky-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-sky-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Bank Account Details</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Customers should transfer to this account:
              </p>
              <div className="mt-2 space-y-1 text-sm">
                <p><strong>Bank:</strong> Moniepoint MFB</p>
                <p><strong>Account Number:</strong> 5229448579</p>
                <p><strong>Account Name:</strong> Factual Medical Diagnostics Services</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Payments List */}
        <div className="card-clinical">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="spinner" />
            </div>
          ) : pendingPayments.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">All Caught Up!</h3>
              <p className="text-muted-foreground mt-1">No pending payments to confirm</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingPayments.map((booking) => (
                <div 
                  key={booking.id} 
                  className="border border-border rounded-lg p-4 hover:border-sky-300 transition-colors"
                  data-testid={`pending-payment-${booking.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                        <CreditCard className="w-6 h-6 text-amber-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-foreground">
                            Booking #{booking.id?.slice(0, 8)}
                          </h3>
                          <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                            Awaiting Confirmation
                          </Badge>
                        </div>
                        
                        <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span>{booking.patient?.full_name || 'Unknown Patient'}</span>
                            <span className="text-xs">({booking.patient?.email || booking.patient?.phone})</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{booking.appointment_date} at {booking.appointment_time}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              {booking.booking_type?.replace('_', ' ') || 'Online'}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {booking.payment_gateway || 'Not specified'}
                            </Badge>
                            {booking.payment_reference && (
                              <span className="text-xs font-mono bg-muted px-2 py-1 rounded">
                                Ref: {booking.payment_reference}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-sky-600">
                          {formatPrice(booking.total_amount, booking.currency)}
                        </p>
                        <p className="text-xs text-muted-foreground">via {booking.payment_gateway}</p>
                      </div>
                      <Button
                        onClick={() => handleConfirmPayment(booking.id)}
                        disabled={confirmingId === booking.id}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`confirm-payment-${booking.id}`}
                      >
                        {confirmingId === booking.id ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Confirming...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Confirm Payment
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminPaymentsPage;
